import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCategorywiseRevenue } from "../../../state/reducers/levelRevenueSlice";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";

const LeadRevenueChart = () => {
  const dispatch = useDispatch();
  const { monthly, yearly, status, error } = useSelector(
    (state) => state.categorywiseRevenue
  );

  const [groupBy, setGroupBy] = useState("month"); // Default grouping: month
  const [selectedMonth, setSelectedMonth] = useState("January"); // Default month: January
  const [reportType, setReportType] = useState("Analytics"); // Default to Analytics view
  const [generateClicked, setGenerateClicked] = useState(false);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  useEffect(() => {
    dispatch(fetchCategorywiseRevenue(groupBy));
  }, [dispatch, groupBy]);

  const handleGenerate = () => {
    setGenerateClicked(true);
  };

  const getChartData = () => {
    if (status === "succeeded") {
      if (groupBy === "month" && monthly) {
        const categories = monthly.map((record) => record.category);
        const revenues = monthly.map((record) =>
          record.yearly_data.flatMap((yearData) =>
            yearData.monthly_revenue
              .filter((monthData) => monthData.month === selectedMonth)
              .reduce((sum, monthData) => sum + monthData.total_revenue, 0)
          )
        );

        return {
          labels: categories,
          datasets: [
            {
              label: `Monthly Revenue for ${selectedMonth}`,
              data: revenues,
              backgroundColor: "#1e40af",
              borderColor: "#00008B",
              borderWidth: 1,
            },
          ],
        };
      }

      if (groupBy === "year" && yearly) {
        const categories = yearly.map((record) => record.category);
        const revenues = yearly.map((record) =>
          record.yearly_revenue.reduce(
            (sum, yearData) => sum + yearData.total_revenue,
            0
          )
        );

        return {
          labels: categories,
          datasets: [
            {
              label: `Yearly Revenue`,
              data: revenues,
              backgroundColor: "#1e40af",
              borderColor: "#00008B",
              borderWidth: 1,
            },
          ],
        };
      }
    }

    return {
      labels: [],
      datasets: [
        {
          label: "No Data Available",
          data: [],
        },
      ],
    };
  };

  const renderList = () => {
    if (generateClicked && status === "succeeded") {
      let revenueData = [];

      if (groupBy === "month" && monthly) {
        revenueData = monthly.flatMap((record) =>
          record.yearly_data.flatMap((yearData) =>
            yearData.monthly_revenue
              .filter((monthData) => monthData.month === selectedMonth)
              .map((monthData) => ({
                category: record.category,
                month: monthData.month,
                total_revenue: monthData.total_revenue,
              }))
          )
        );
      } else if (groupBy === "year" && yearly) {
        revenueData = yearly.flatMap((record) =>
          record.yearly_revenue.map((yearData) => ({
            category: record.category,
            year: yearData.year,
            total_revenue: yearData.total_revenue,
          }))
        );
      }

      if (revenueData.length === 0) {
        return (
          <div className="text-center text-gray-500">No data available</div>
        );
      }

      return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h3 className="text-xl font-semibold mb-4">Revenue Data</h3>
          <table className="min-w-full">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left">Category</th>
                <th className="px-4 py-2 text-left">
                  {groupBy === "month" ? "Month" : "Year"}
                </th>
                <th className="px-4 py-2 text-left">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {revenueData.map((item, index) => (
                <tr key={index}>
                  <td className="px-4 py-2">{item.category}</td>
                  <td className="px-4 py-2">{item.month || item.year}</td>
                  <td className="px-4 py-2">{item.total_revenue}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return <div className="text-center text-gray-500">No data available</div>;
  };

  const chartData = getChartData();

  return (
    <div className="bg-white p-6 rounded-md shadow-md">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-bold">Categorywise Revenue Report</h1>
        <div className="flex gap-4">
          <button
            onClick={() => setGroupBy("month")}
            className={`px-4 py-2 rounded-md ${
              groupBy === "month"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setGroupBy("year")}
            className={`px-4 py-2 rounded-md ${
              groupBy === "year"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            Yearly
          </button>
        </div>
      </div>

      <div className="flex gap-4">
        {groupBy === "month" && (
          <select
            className="px-4 py-2 rounded-md"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            {months.map((month) => (
              <option key={month} value={month}>
                {month}
              </option>
            ))}
          </select>
        )}
        <select
          className="px-4 py-2 rounded-md"
          value={reportType}
          onChange={(e) => setReportType(e.target.value)}
        >
          <option value="Analytics">Analytics</option>
          <option value="List">List</option>
        </select>
        <button
          onClick={handleGenerate}
          className="bg-blue-500 text-white px-4 py-2 rounded"
        >
          Generate
        </button>
      </div>

      {status === "loading" && <p className="text-gray-500">Loading...</p>}
      {status === "failed" && <p className="text-red-500">{error}</p>}

      {reportType === "Analytics" ? (
        <Bar
          data={chartData}
          options={{
            responsive: true,
            plugins: {
              legend: { display: true },
            },
            scales: {
              y: {
                beginAtZero: true,
              },
            },
          }}
        />
      ) : (
        renderList()
      )}
    </div>
  );
};

export default LeadRevenueChart;
